import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import svgSprite from '@syskit/sico/symbol-defs.svg';

import appInsights from './common/applicationInsights';
import { ErrorScreen } from './components/errorBoundary/errorScreen';
import Spinner, { ClipSpinner } from './components/spinner';
import SvgSpriteRenderer from './components/svgSpriteRenderer/svgSpriteRenderer';
import App from './containers/app';
import { SettingsProvider } from './contexts/SettingsContext';
import { useAsync } from './hooks/useAsync';
import { AppClient, ClientConfigDto } from './services/service';
import settings from './settings/settings';

import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import './assets/style/devExtreme/devExtreme.scss';
import './assets/style/devExtreme/devExtremeGlobalOverrides.scss';

import './index.scss';

const AppSettings: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { executeAsync, isLoading, error, data } = useAsync<ClientConfigDto>();

    useEffect(() => {
        const appClient = new AppClient();
        appClient.isAnonymous = true;

        executeAsync(() => appClient.getClientConfig())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize settings and appInsights when data is available
    useEffect(() => {
        if (data) {
            settings.setSettings(data);
            appInsights.initialize(settings.AppInsightsConnectionString);
        }
    }, [data]);

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (error) {
        return <ErrorScreen error={error} />
    }

    return (
        <SettingsProvider settings={data} isLoading={isLoading}>
            {children}
        </SettingsProvider>
    );
}

const root = createRoot(document.getElementById('app')!);

root.render(
    <AppSettings>
        <SvgSpriteRenderer svgContent={svgSprite} />
        <App />
    </AppSettings>
)

export const LoadingSpinner: React.FC = () => (
    <Spinner text='Loading...'>
        <ClipSpinner size={70} color={'var(--electric-indigo)'} />
    </Spinner>
);